import { lonLatToLocal } from './view-sync';

/**
 * Pure helpers that turn an XYZ dataset (GET /api/datasets/{id}/points, a GeoJSON FeatureCollection
 * in lon/lat) into one compact point cloud in the 3D viewer's local frame (the same tangent plane
 * the fences and voxels use, see lonLatToLocal in view-sync.js). No React, no three.js.
 */

// Same cool-to-warm ramp the voxel clouds use.
const STOPS = [[37, 99, 235], [6, 182, 212], [52, 211, 153], [251, 191, 36], [251, 146, 60], [248, 113, 113]];

/** True when the dataset row has a saved column mapping with at least one z column. */
export function hasXyz(dataset) {
  const m = dataset?.column_mapping;
  return !!m && Array.isArray(m.z) && m.z.length > 0;
}

/**
 * Compact arrays from the points response: lon/lat as Float64 (precision at 8 decimals) and z as
 * Float32. The z column is `zField` or the first of metadata.xyz.zFields; features without finite
 * coordinates or z are dropped. A depth-named column plots downward (z negated).
 */
export function extractLonLatZ(fc, zField) {
  const feats = fc?.features || [];
  const field = zField || fc?.metadata?.xyz?.zFields?.[0];
  const n = feats.length;
  const lon = new Float64Array(n), lat = new Float64Array(n), z = new Float32Array(n);
  const sign = field && /depth/i.test(field) ? -1 : 1;
  let count = 0;
  for (let i = 0; i < n; i++) {
    const f = feats[i];
    const c = f?.geometry?.coordinates;
    const v = field ? Number(f?.properties?.[field]) : NaN;
    if (!c || !Number.isFinite(c[0]) || !Number.isFinite(c[1]) || !Number.isFinite(v)) continue;
    lon[count] = c[0]; lat[count] = c[1]; z[count] = sign * v;
    count++;
  }
  return { lon: lon.subarray(0, count), lat: lat.subarray(0, count), z: z.subarray(0, count), count, field: field || null, depth: sign < 0 };
}

/** Centroid origin { lon0, lat0 } of an extract, or null when empty. */
export function originOf(ext) {
  if (!ext?.count) return null;
  let sx = 0, sy = 0;
  for (let i = 0; i < ext.count; i++) { sx += ext.lon[i]; sy += ext.lat[i]; }
  return { lon0: sx / ext.count, lat0: sy / ext.count };
}

/**
 * Vertical frame of a point cloud: (lon, lat, z, isDepth) -> scene y. Without a terrain reference it is
 * y = z (a depth column is already negated). With `refElev` (the terrain mesh's reference elevation) an
 * elevation z lands at z - refElev, the same frame as the terrain, fences and voxels, so z = DEM height
 * sits on the surface; a depth column hangs below the ground at that point (`groundY(lon, lat)`, y of the
 * DEM before exaggeration, else the reference). The vertical exaggeration is not applied here: the
 * terrain group that hosts the cloud already scales y by ve3d.
 */
export function verticalFrame({ refElev = null, groundY = null } = {}) {
  if (!Number.isFinite(refElev)) return (lon, lat, z) => z;
  return (lon, lat, z, isDepth) => (isDepth ? (groundY ? groundY(lon, lat) : 0) + z : z - refElev);
}

/**
 * One position buffer (x east, y = z value, z north) and one colour buffer (z mapped onto the ramp)
 * for a whole dataset. Two typed arrays per dataset, no per-point objects.
 */
export function buildPointBuffers(ext, origin, frame = null) {
  const n = ext?.count || 0;
  if (!n || !origin) return { positions: new Float32Array(0), colors: new Float32Array(0), count: 0, bboxMin: [0, 0, 0], bboxMax: [0, 0, 0] };
  const positions = new Float32Array(n * 3);
  const colors = new Float32Array(n * 3);
  let zMin = Infinity, zMax = -Infinity;
  for (let i = 0; i < n; i++) { const v = ext.z[i]; if (v < zMin) zMin = v; if (v > zMax) zMax = v; }
  const span = zMax > zMin ? zMax - zMin : 1;
  const bboxMin = [Infinity, Infinity, Infinity], bboxMax = [-Infinity, -Infinity, -Infinity];
  const toY = frame || verticalFrame();
  const last = STOPS.length - 1;
  const pt = [0, 0];
  for (let i = 0; i < n; i++) {
    pt[0] = ext.lon[i]; pt[1] = ext.lat[i];
    const [x, zz] = lonLatToLocal(pt, origin);
    const y = toY(ext.lon[i], ext.lat[i], ext.z[i], !!ext.depth);
    positions[i * 3] = x; positions[i * 3 + 1] = y; positions[i * 3 + 2] = zz;
    if (y < bboxMin[1]) bboxMin[1] = y;
    if (y > bboxMax[1]) bboxMax[1] = y;
    if (x < bboxMin[0]) bboxMin[0] = x;
    if (x > bboxMax[0]) bboxMax[0] = x;
    if (zz < bboxMin[2]) bboxMin[2] = zz;
    if (zz > bboxMax[2]) bboxMax[2] = zz;
    const p = ((ext.z[i] - zMin) / span) * last;
    const lo = Math.min(last - 1, Math.floor(p)), fr = p - lo;
    for (let k = 0; k < 3; k++) colors[i * 3 + k] = (STOPS[lo][k] + (STOPS[lo + 1][k] - STOPS[lo][k]) * fr) / 255;
  }
  return { positions, colors, count: n, bboxMin, bboxMax };
}
